import EmailIcon from '@mui/icons-material/Email'
import FacebookIcon from '@mui/icons-material/Facebook'
import InstagramIcon from '@mui/icons-material/Instagram'
import LocalPhoneIcon from '@mui/icons-material/LocalPhone'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    styled,
} from '@mui/material'
import { Stack } from '@mui/system'
import QRCode from 'qrcode'
import { useEffect, useRef } from 'react'
import { useBranch } from '../../context/branch_context'
import { PaymentType } from '../../context/buy_context'
import { ClientDocumentType } from '../../context/client_context'
import { CoinInfo } from '../../context/coin_context'
import {
    PrintMethodEnum,
    PromissoryNodeMode,
    useConfigs,
} from '../../context/configs_context'
import { SaleInfo, SaleStatus } from '../../context/sale_context'
import {
    getDateInDDMMYYYYFormat,
    getDateString,
    getIvaBaseExe,
    getIvaTenSubtotal,
    getNoteNameCharLimit,
    maskCurrency,
    numeroPorExtensoEspanhol,
} from '../../utils/maks'
import { Rect } from '../PrintPositionModal'

const StyledTableCell = styled(TableCell)(({}) => ({
    color: 'text.primary',
    border: 'none',
    padding: '1px 2px',
    fontSize: '11px',
}))

interface LegalNoteProps {
    sale: SaleInfo
    clientVia: boolean
    index: number
    selectedCoin?: CoinInfo
    hasRendered(): void
}

const ContactIcon: Record<string, JSX.Element> = {
    Facebook: <FacebookIcon />,
    Instagram: <InstagramIcon />,
    Whatsapp: <WhatsAppIcon />,
    Endereço: <LocationOnIcon />,
    Telefone: <LocalPhoneIcon />,
    Email: <EmailIcon />,
}

function LegalNote({
    sale,
    clientVia,
    index,
    selectedCoin,
    hasRendered,
}: LegalNoteProps) {
    const { configs, contactTypes, base64Logo } = useConfigs()
    const qrCodeRef = useRef<HTMLCanvasElement | null>(null)
    const timeoutRef = useRef<NodeJS.Timeout>()
    const { branches } = useBranch()

    const contacts = sale.branch ? sale.branch.contacts : configs.contacts
    const companyName =
        sale.branch && configs.showBranchNameInNote
            ? sale.branch.name
            : configs.companyName
    const companyLogo = sale.branch?.companyLogo
        ? branches.find((b) => b.id === sale.branch?.id)?.companyLogo?.content
        : base64Logo
    const companySlogan = sale.branch?.companySlogan
        ? sale.branch.companySlogan
        : configs.companySlogan
    const companyDocument = sale.branch?.companyDocument
        ? sale.branch.companyDocument
        : configs.companyDocument

    const totals =
        selectedCoin?.id !== sale.coin?.id
            ? sale.totalSecondaryCoin?.find(
                  (v) => v.coinId === selectedCoin?.id
              )!
            : sale

    useEffect(() => {
        if (sale.qrCode)
            QRCode.toCanvas(qrCodeRef.current, sale.qrCode, {
                width: 175,
            }).then(hasRendered)
        else hasRendered()
    }, [])

    if (
        configs.printMethod === PrintMethodEnum.DataOnly &&
        configs.printPosition
    ) {
        const printData = JSON.parse(configs.printPosition)
        const boxes = (printData.boxes ?? printData) as Rect[]
        const isHorizontal = printData.isHorizontal
        const noteConfigs = printData.noteConfigs?.[index]
        const noteSize = noteConfigs ? noteConfigs.size : 100 / configs.notesQnt
        const paddintTop = noteConfigs ? noteConfigs.paddingTop : 0

        setTimeout(() => {
            const dataPrint = document.getElementById(
                `data-print-${sale.id}-${index}`
            )
            if (!dataPrint) return

            dataPrint.innerHTML = ''

            boxes
                .filter((box) => box.isActive)
                .forEach((box) => {
                    const fontFamily = box.fontFamily
                    const fontSize = `${box.fontSize}px`
                    const el = document.createElement('div')
                    el.style.position = 'absolute'
                    el.style.fontSize = fontSize
                    el.style.fontFamily = fontFamily
                    el.style.fontWeight = box.isBold ? 'bold' : '400'

                    if (isHorizontal) {
                        el.style.right = `${box.yProp * 100}%`
                        el.style.top = `${box.xProp * 100 + paddintTop}%`
                        el.style.transform =
                            'rotate(0.25turn) translate(100%, 0)'
                        el.style.transformOrigin = '100% 0'
                    } else {
                        el.style.left = `${box.xProp * 100}%`
                        el.style.top = `${box.yProp * 100 + paddintTop}%`
                    }

                    if (box.text === 'código') {
                        el.style.transform = ''
                        el.style.transformOrigin = ''

                        for (let i = 0; i < sale.products.length; i++) {
                            const product = sale.products[i]
                            const noteProduct =
                                product.noteProduct ?? product.product

                            const p = document.createElement('p')
                            p.style.fontSize = fontSize
                            p.style.fontFamily = fontFamily
                            p.style.position = 'absolute'
                            p.style.whiteSpace = 'nowrap'

                            if (isHorizontal) {
                                p.style.transform =
                                    'rotate(0.25turn) translate(100%, 0)'
                                p.style.transformOrigin = '100% 0'
                                p.style.right = `${i * 15}px`
                            } else {
                                p.style.top = `${i * 15}px`
                            }

                            p.innerHTML = noteProduct.internalCodes[0] ?? ''

                            el.appendChild(p)
                        }
                    }

                    if (box.text === 'cod bar') {
                        el.style.transform = ''
                        el.style.transformOrigin = ''

                        for (let i = 0; i < sale.products.length; i++) {
                            const product = sale.products[i]
                            const noteProduct =
                                product.noteProduct ?? product.product

                            const p = document.createElement('p')
                            p.style.fontSize = fontSize
                            p.style.fontFamily = fontFamily
                            p.style.position = 'absolute'
                            p.style.whiteSpace = 'nowrap'

                            if (isHorizontal) {
                                p.style.transform =
                                    'rotate(0.25turn) translate(100%, 0)'
                                p.style.transformOrigin = '100% 0'
                                p.style.right = `${i * 15}px`
                            } else {
                                p.style.top = `${i * 15}px`
                            }

                            p.innerHTML = noteProduct.barcodes[0]

                            el.appendChild(p)
                        }
                    }

                    if (box.text === 'desc') {
                        el.style.transform = ''
                        el.style.transformOrigin = ''

                        for (let i = 0; i < sale.products.length; i++) {
                            const product = sale.products[i]
                            const noteProduct =
                                product.noteProduct ?? product.product

                            const p = document.createElement('p')
                            p.style.fontSize = fontSize
                            p.style.fontFamily = fontFamily
                            p.style.position = 'absolute'
                            p.style.whiteSpace = 'nowrap'

                            if (isHorizontal) {
                                p.style.transform =
                                    'rotate(0.25turn) translate(100%, 0)'
                                p.style.transformOrigin = '100% 0'
                                p.style.right = `${i * 15}px`
                            } else {
                                p.style.top = `${i * 15}px`
                            }

                            p.innerHTML = noteProduct.name.slice(0, 30)

                            el.appendChild(p)
                        }
                    }

                    if (box.text === 'qnt') {
                        el.style.transform = ''
                        el.style.transformOrigin = ''

                        for (let i = 0; i < sale.products.length; i++) {
                            const product = sale.products[i]

                            const p = document.createElement('p')
                            p.style.fontSize = fontSize
                            p.style.fontFamily = fontFamily
                            p.style.position = 'absolute'
                            p.style.whiteSpace = 'nowrap'

                            if (isHorizontal) {
                                p.style.transform =
                                    'rotate(0.25turn) translate(100%, 0)'
                                p.style.transformOrigin = '100% 0'
                                p.style.right = `${i * 15}px`
                            } else {
                                p.style.top = `${i * 15}px`
                            }

                            p.innerHTML = product.qnt.toString()

                            el.appendChild(p)
                        }
                    }

                    if (box.text === 'p un') {
                        el.style.transform = ''
                        el.style.transformOrigin = ''

                        for (let i = 0; i < sale.products.length; i++) {
                            const product = sale.products[i]

                            const p = document.createElement('p')
                            p.style.fontSize = fontSize
                            p.style.fontFamily = fontFamily
                            p.style.position = 'absolute'
                            p.style.whiteSpace = 'nowrap'

                            if (isHorizontal) {
                                p.style.transform =
                                    'rotate(0.25turn) translate(100%, 0)'
                                p.style.transformOrigin = '100% 0'
                                p.style.right = `${i * 15}px`
                            } else {
                                p.style.top = `${i * 15}px`
                            }

                            p.innerHTML = maskCurrency(
                                product.notePrice,
                                sale.coin
                            )

                            el.appendChild(p)
                        }
                    }

                    if (box.text === 'p ex') {
                        el.style.transform = ''
                        el.style.transformOrigin = ''

                        for (let i = 0; i < sale.products.length; i++) {
                            const product = sale.products[i]

                            const p = document.createElement('p')
                            p.style.fontSize = fontSize
                            p.style.fontFamily = fontFamily
                            p.style.position = 'absolute'
                            p.style.whiteSpace = 'nowrap'

                            if (isHorizontal) {
                                p.style.transform =
                                    'rotate(0.25turn) translate(100%, 0)'
                                p.style.transformOrigin = '100% 0'
                                p.style.right = `${i * 15}px`
                            } else {
                                p.style.top = `${i * 15}px`
                            }

                            p.innerHTML = maskCurrency(
                                product.ivaValue === 0
                                    ? product.notePrice * product.qnt
                                    : getIvaBaseExe(
                                          product.ivaValue ?? 0,
                                          product.notePrice * product.qnt,
                                          product.proporcaoIva ?? 100
                                      ),
                                sale.coin
                            )

                            el.appendChild(p)
                        }
                    }

                    if (box.text === 'p 5%') {
                        el.style.transform = ''
                        el.style.transformOrigin = ''

                        for (let i = 0; i < sale.products.length; i++) {
                            const product = sale.products[i]

                            const p = document.createElement('p')
                            p.style.fontSize = fontSize
                            p.style.fontFamily = fontFamily
                            p.style.position = 'absolute'
                            p.style.whiteSpace = 'nowrap'

                            if (isHorizontal) {
                                p.style.transform =
                                    'rotate(0.25turn) translate(100%, 0)'
                                p.style.transformOrigin = '100% 0'
                                p.style.right = `${i * 15}px`
                            } else {
                                p.style.top = `${i * 15}px`
                            }

                            p.innerHTML = maskCurrency(
                                product.ivaValue === 5
                                    ? product.notePrice * product.qnt
                                    : 0,
                                sale.coin
                            )

                            el.appendChild(p)
                        }
                    }

                    if (box.text === 'p 10%') {
                        el.style.transform = ''
                        el.style.transformOrigin = ''

                        for (let i = 0; i < sale.products.length; i++) {
                            const product = sale.products[i]

                            const p = document.createElement('p')
                            p.style.fontSize = fontSize
                            p.style.fontFamily = fontFamily
                            p.style.position = 'absolute'
                            p.style.whiteSpace = 'nowrap'

                            if (isHorizontal) {
                                p.style.transform =
                                    'rotate(0.25turn) translate(100%, 0)'
                                p.style.transformOrigin = '100% 0'
                                p.style.right = `${i * 15}px`
                            } else {
                                p.style.top = `${i * 15}px`
                            }

                            p.innerHTML = maskCurrency(
                                product.ivaValue === 10
                                    ? getIvaTenSubtotal(
                                          product.ivaValue,
                                          product.notePrice,
                                          product.qnt,
                                          product.proporcaoIva
                                      )
                                    : 0,
                                sale.coin
                            )

                            el.appendChild(p)
                        }
                    }

                    if (box.text === 'nome cl') {
                        el.innerHTML = sale.client?.name ?? ''
                    }

                    if (box.text === 'doc cl') {
                        el.innerHTML = sale.client?.document ?? ''
                    }

                    if (box.text === 'dir cl') {
                        el.innerHTML = sale.client?.address ?? ''
                    }

                    if (box.text === 'tel cl') {
                        el.innerHTML = sale.client?.phoneNumbers[0] ?? ''
                    }

                    if (box.text === 'cond') {
                        el.innerHTML =
                            (sale.paymentTypeShow === PaymentType.Credit ||
                                sale.paymentTypeShow === PaymentType.Parcel) &&
                            !configs.showOnlyNormalPaymentType
                                ? 'CRÉDITO'
                                : 'CONTADO'
                    }

                    if (box.text === 'cond x') {
                        el.innerHTML = 'X'

                        if (box.version === 2) {
                            if (
                                sale.paymentTypeShow === PaymentType.Credit ||
                                sale.paymentTypeShow === PaymentType.Parcel
                            ) {
                                el.style.left = `${box.yProp * 100 + 3}%`
                            }
                        } else {
                            if (
                                sale.paymentTypeShow === PaymentType.Credit ||
                                sale.paymentTypeShow === PaymentType.Parcel
                            ) {
                                el.style.top = `${box.yProp * 100 + 1.5}%`
                            }
                        }
                    }

                    if (box.text === 'data') {
                        const date = getDateString({
                            date: sale.date,
                            hour: sale.hour,
                            configs: configs,
                            isSummerTime: !!sale.isSummerTime,
                        })

                        const meses = [
                            'ENERO',
                            'FEBRERO',
                            'MARZO',
                            'ABRIL',
                            'MAYO',
                            'JUNIO',
                            'JULIO',
                            'AGOSTO',
                            'SEPTIEMBRE',
                            'OCTUBRE',
                            'NOVIEMBRE',
                            'DICIEMBRE',
                        ]

                        const [dd, mm, yy] = date.split(' ')[0].split('/')

                        if (box.version === 2) {
                            el.innerHTML = `${dd} DE ${
                                meses[Number(mm) - 1]
                            } DE ${yy}`
                        } else {
                            el.style.display = 'flex'
                            el.style.gap = '120px'

                            const ddEl = document.createElement('p')
                            ddEl.style.fontSize = fontSize
                            ddEl.style.fontFamily = fontFamily
                            ddEl.innerHTML = dd

                            const mmEl = document.createElement('p')
                            mmEl.style.fontSize = fontSize
                            mmEl.style.fontFamily = fontFamily
                            mmEl.innerHTML = meses[Number(mm) - 1]

                            const yyEl = document.createElement('p')
                            yyEl.style.fontSize = fontSize
                            yyEl.style.fontFamily = fontFamily
                            yyEl.innerHTML = yy

                            el.appendChild(ddEl)
                            el.appendChild(mmEl)
                            el.appendChild(yyEl)
                        }
                    }

                    if (box.text === 'sub ex') {
                        el.innerHTML = maskCurrency(
                            sale.totalExcept ?? 0,
                            sale.coin
                        )
                    }

                    if (box.text === 'sub 5%') {
                        el.innerHTML = maskCurrency(
                            sale.totalFivePercent ?? 0,
                            sale.coin
                        )
                    }

                    if (box.text === 'sub 10%') {
                        el.innerHTML = maskCurrency(
                            sale.totalTenPercent ?? 0,
                            sale.coin
                        )
                    }

                    if (box.text === 'iva 5%') {
                        el.innerHTML = maskCurrency(
                            sale.totalIvaFivePercent ?? 0,
                            sale.coin
                        )
                    }

                    if (box.text === 'iva 10%') {
                        el.innerHTML = maskCurrency(
                            sale.totalIvaTenPercent ?? 0,
                            sale.coin
                        )
                    }

                    if (box.text === 'iva tot') {
                        el.innerHTML = maskCurrency(
                            sale.totalIva ?? 0,
                            sale.coin
                        )
                    }

                    if (box.text === 'total ext') {
                        el.style.width = '100%'
                        el.innerHTML = `${numeroPorExtensoEspanhol(
                            sale?.total,
                            sale.coin
                        )}`
                    }

                    if (box.text === 'total') {
                        el.innerHTML = maskCurrency(sale.total ?? 0, sale.coin)
                    }

                    if (box.text.endsWith(':')) {
                        el.innerHTML = box.text
                    }

                    dataPrint.appendChild(el)
                })
        }, 100)

        return (
            <Stack
                direction="column"
                width={'100%'}
                height={`${noteSize}vh`}
                id={`data-print-${sale.id}-${index}`}
                position={'relative'}
                /* sx={{ transform: 'rotate(90deg)' }} */
            ></Stack>
        )
    }

    const charNameLimit = getNoteNameCharLimit(configs)
    const isEletronicNote = !!sale.qrCode

    return (
        <Stack direction="column" width="100%" px="20px" py="10px">
            {isEletronicNote && (
                <Stack
                    direction={'row'}
                    width={'100%'}
                    border={'1px solid black'}
                    borderBottom={'0'}
                    p={'5px'}
                    sx={{ background: '#a0a0a0' }}
                >
                    <Typography
                        textAlign={'center'}
                        width={'100%'}
                        fontWeight={'600'}
                        fontSize={'14px'}
                    >
                        KuDE de Factura Electrónica
                    </Typography>
                </Stack>
            )}
            <Stack direction={'row'} width={'100%'}>
                <Stack
                    direction={'column'}
                    border={'1px solid black'}
                    borderRight={'0'}
                    borderRadius={'12px'}
                    width={'70%'}
                    sx={{
                        borderTopLeftRadius: isEletronicNote ? '0' : undefined,
                        borderTopRightRadius: isEletronicNote ? '0' : undefined,
                    }}
                >
                    <Stack direction={'row'} p={'10px'}>
                        <Box
                            component={'img'}
                            src={companyLogo}
                            width={'220px'}
                            height={'110px'}
                            zIndex={'-1'}
                        />
                        <Stack textAlign={'center'} flex={'1'} px={'15px'}>
                            <Typography
                                fontSize={'14px'}
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {companyName}
                            </Typography>
                            <Typography
                                fontSize={'9px'}
                                sx={{ wordBreak: 'break-word' }}
                                fontStyle={'italic'}
                                whiteSpace={'pre-line'}
                            >
                                {`${companySlogan}`}
                            </Typography>
                            <Typography
                                fontSize={'8px'}
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {contactTypes
                                    .filter((ct) =>
                                        contacts.find(
                                            (c) => c.contactTypeId === ct.id
                                        )
                                    )
                                    .map(
                                        (ct) =>
                                            `${ct.name}: ${contacts
                                                .filter(
                                                    (c) =>
                                                        c.contactTypeId ===
                                                        ct.id
                                                )
                                                .map((c) => c.value)
                                                .join(', ')}`
                                    )
                                    .join('; ')}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
                <Stack
                    direction={'column'}
                    border={'1px solid black'}
                    borderRadius={'12px'}
                    width={'30%'}
                    sx={{
                        borderTopLeftRadius: isEletronicNote ? '0' : undefined,
                        borderTopRightRadius: isEletronicNote ? '0' : undefined,
                    }}
                >
                    <Stack direction={'row'} p={'10px'}>
                        <Box flex={'1'}>
                            <Typography fontSize={'11px'}>
                                TIMBRADO N {sale.note?.timbrado}
                            </Typography>
                            <Typography fontSize={'11px'} mt={'1px'}>
                                RUC: {companyDocument}
                            </Typography>
                            <Typography fontSize={'11px'} mt={'1px'}>
                                Fecha início vigência:{' '}
                                {getDateInDDMMYYYYFormat(
                                    sale.note?.iniD as any
                                )}
                            </Typography>
                            <Typography fontSize={'11px'}>
                                Fecha fin vigência:{' '}
                                {sale.note?.endD
                                    ? getDateInDDMMYYYYFormat(sale.note.endD)
                                    : '---'}
                            </Typography>
                            <Typography
                                fontSize={'14px'}
                                mt={'5px'}
                                fontWeight={'bold'}
                                textAlign={'center'}
                            >
                                {sale.bill}
                            </Typography>
                            <Typography
                                fontSize={'16px'}
                                fontWeight={'bold'}
                                textAlign={'center'}
                            >
                                {isEletronicNote
                                    ? !!sale.client?.informacoesAdicionais
                                        ? 'FACTURA ELECTRÓNICA DE EXPORTACIÓN'
                                        : 'FACTURA ELECTRÓNICA'
                                    : 'FACTURA'}
                            </Typography>
                        </Box>
                        {/* <Typography
                            sx={{
                                writingMode: 'vertical-rl',
                                textOrientation: 'mixed',
                            }}
                            p={'30px'}
                            color={'error'}
                            fontSize={'26px'}
                        >
                            {sale.bill}
                        </Typography> */}
                    </Stack>
                </Stack>
            </Stack>
            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderTop={'0'}
                borderRadius={'12px'}
                direction={'column'}
                padding={'5px'}
            >
                <Stack direction={'row'} alignItems={'center'}>
                    <Box width={'70%'}>
                        <Typography fontSize={'16px'} fontWeight={'bold'}>
                            Nombre: {sale.client?.name || '---'}
                        </Typography>
                        <Typography fontSize={'16px'} fontWeight={'bold'}>
                            {sale.client?.documentType ===
                            ClientDocumentType.RUC
                                ? 'Ruc'
                                : 'Documento'}
                            : {sale.client?.document || '---'}
                        </Typography>
                    </Box>
                    <Box>
                        <Box>
                            <Typography fontSize={'11px'}>
                                Cond. de venta:{' '}
                                {(sale.paymentTypeShow === PaymentType.Credit ||
                                    sale.paymentTypeShow ===
                                        PaymentType.Parcel) &&
                                !configs.showOnlyNormalPaymentType
                                    ? 'CRÉDITO'
                                    : 'CONTADO'}
                            </Typography>
                            <Typography fontSize={'11px'}>
                                Fecha:{' '}
                                {
                                    getDateString({
                                        date: sale.noteDate,
                                        hour: sale.noteHour,
                                        configs: configs,
                                        isSummerTime: !!sale.isNoteSummerTime,
                                    }).split(' ')[0]
                                }
                            </Typography>
                            <Typography fontSize={'11px'}>
                                Telefonos:{' '}
                                {sale.client?.phoneNumbers.join(', ') || '---'}
                            </Typography>
                            <Typography fontSize={'11px'}>
                                Dirección: {sale.client?.address || '---'}
                            </Typography>
                        </Box>
                    </Box>
                </Stack>
                {isEletronicNote && !!sale.client?.informacoesAdicionais && (
                    <Typography fontSize={'11px'} mt={'5px'}>
                        <Box component={'b'}>Información fiscal:</Box>{' '}
                        {sale.client.informacoesAdicionais.replace(/\n/g, ' ')}
                    </Typography>
                )}
            </Stack>
            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderTop={'0'}
                borderRadius={'12px'}
                direction={'column'}
                flex={'1'}
                justifyContent={'space-between'}
            >
                <Table>
                    <TableHead>
                        <TableRow>
                            {configs.showIdOnNote && (
                                <StyledTableCell
                                    sx={{
                                        borderBottom: '1px solid black',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    CÓDIGO
                                </StyledTableCell>
                            )}
                            {configs.showBarcodeOnNote && (
                                <StyledTableCell
                                    sx={{
                                        borderBottom: '1px solid black',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    CÓDIGO BAR.
                                </StyledTableCell>
                            )}
                            {configs.showNcmOnNote && (
                                <StyledTableCell
                                    sx={{
                                        borderBottom: '1px solid black',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    NCM
                                </StyledTableCell>
                            )}
                            <StyledTableCell
                                sx={{
                                    borderBottom: '1px solid black',
                                    fontWeight: 'bold',
                                }}
                            >
                                CANT. / DESC.
                            </StyledTableCell>
                            {configs.showUnitPriceOnNote && (
                                <StyledTableCell
                                    sx={{
                                        borderBottom: '1px solid black',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    P. UNITÁRIO
                                </StyledTableCell>
                            )}
                            <StyledTableCell
                                sx={{
                                    borderBottom: '1px solid black',
                                    fontWeight: 'bold',
                                }}
                            >
                                P. VENTA
                            </StyledTableCell>
                            {configs.showDiscountPercentageOnNote && (
                                <StyledTableCell
                                    sx={{
                                        borderBottom: '1px solid black',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    DESC. %
                                </StyledTableCell>
                            )}
                            <StyledTableCell
                                sx={{
                                    borderBottom: '1px solid black',
                                    fontWeight: 'bold',
                                }}
                            >
                                EXENTO
                            </StyledTableCell>
                            <StyledTableCell
                                sx={{
                                    borderBottom: '1px solid black',
                                    fontWeight: 'bold',
                                }}
                            >
                                5%
                            </StyledTableCell>
                            <StyledTableCell
                                sx={{
                                    borderRight: 'none',
                                    borderBottom: '1px solid black',
                                    fontWeight: 'bold',
                                }}
                            >
                                10%
                            </StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sale.products.map((product) => {
                            const noteProduct =
                                product.noteProduct ?? product.product
                            const values =
                                selectedCoin?.id !== sale.coin?.id
                                    ? product.valueSecondaryCoin?.find(
                                          (v) => v.coinId === selectedCoin?.id
                                      )!
                                    : product

                            const subtotal = values?.notePrice * product.qnt

                            const name = configs.showObservationOnNote
                                ? noteProduct.parsedName
                                : noteProduct.name

                            return (
                                <TableRow sx={{ height: '0' }}>
                                    {configs.showIdOnNote && (
                                        <StyledTableCell>
                                            {noteProduct.internalCodes[0]}
                                        </StyledTableCell>
                                    )}
                                    {configs.showBarcodeOnNote && (
                                        <StyledTableCell>
                                            {noteProduct.barcodes[0] || '---'}
                                        </StyledTableCell>
                                    )}
                                    {configs.showNcmOnNote && (
                                        <StyledTableCell>
                                            {noteProduct.ncm}
                                        </StyledTableCell>
                                    )}
                                    <StyledTableCell>
                                        {product.qnt} /{' '}
                                        <Box
                                            component={'span'}
                                            fontSize={'9px'}
                                            sx={{ wordBreak: 'break-word' }}
                                        >
                                            {name &&
                                            name.length > charNameLimit &&
                                            !configs.ignoreCharacterLimitNote
                                                ? name.slice(0, charNameLimit) +
                                                  '...'
                                                : name}
                                        </Box>
                                    </StyledTableCell>
                                    {configs.showUnitPriceOnNote && (
                                        <StyledTableCell>
                                            {maskCurrency(
                                                values?.unitPrice ?? 0,
                                                selectedCoin
                                            )}
                                        </StyledTableCell>
                                    )}
                                    <StyledTableCell>
                                        {maskCurrency(
                                            values?.notePrice,
                                            selectedCoin
                                        )}
                                    </StyledTableCell>
                                    {configs.showDiscountPercentageOnNote && (
                                        <StyledTableCell>
                                            {values?.unitPrice
                                                ? `${(
                                                      ((values.unitPrice -
                                                          values.notePrice) /
                                                          values.unitPrice) *
                                                      100
                                                  ).toFixed(2)}%`
                                                : '0,00%'}
                                        </StyledTableCell>
                                    )}
                                    <StyledTableCell>
                                        {maskCurrency(
                                            product.ivaValue === 0
                                                ? subtotal
                                                : getIvaBaseExe(
                                                      product.ivaValue ?? 0,
                                                      subtotal,
                                                      product.proporcaoIva ??
                                                          100
                                                  ),
                                            selectedCoin
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {maskCurrency(
                                            product.ivaValue === 5
                                                ? subtotal
                                                : 0,
                                            selectedCoin
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell
                                        sx={{ borderRight: 'none' }}
                                    >
                                        {maskCurrency(
                                            product.ivaValue === 10
                                                ? getIvaTenSubtotal(
                                                      product.ivaValue,
                                                      values?.notePrice,
                                                      product.qnt,
                                                      product.proporcaoIva
                                                  )
                                                : 0,
                                            selectedCoin
                                        )}
                                    </StyledTableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
                <Table>
                    <TableRow>
                        <StyledTableCell
                            sx={{
                                borderTop: '1px solid black',
                                fontSize: '14px',
                            }}
                        >
                            Subtotales:
                        </StyledTableCell>
                        {configs.showIdOnNote && (
                            <StyledTableCell
                                sx={{
                                    borderTop: '1px solid black',
                                }}
                            ></StyledTableCell>
                        )}
                        <StyledTableCell
                            sx={{
                                borderTop: '1px solid black',
                            }}
                        ></StyledTableCell>
                        <StyledTableCell
                            sx={{
                                borderTop: '1px solid black',
                            }}
                        ></StyledTableCell>
                        <StyledTableCell
                            sx={{
                                borderTop: '1px solid black',
                            }}
                        >
                            {maskCurrency(
                                totals?.totalExcept as number,
                                selectedCoin
                            )}
                        </StyledTableCell>
                        <StyledTableCell
                            sx={{
                                borderTop: '1px solid black',
                            }}
                        >
                            {maskCurrency(
                                totals?.totalFivePercent as number,
                                selectedCoin
                            )}
                        </StyledTableCell>
                        <StyledTableCell
                            sx={{
                                borderTop: '1px solid black',
                            }}
                        >
                            {maskCurrency(
                                totals?.totalTenPercent as number,
                                selectedCoin
                            )}
                        </StyledTableCell>
                    </TableRow>
                </Table>
            </Stack>
            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderTop={'0'}
                borderRadius={'12px'}
                pl={'10px'}
                pb={'5px'}
            >
                <Stack direction={'row'} justifyContent={'space-between'}>
                    <Stack>
                        <Typography fontSize={'12px'}>
                            Total a pagar:{' '}
                            <Box component={'span'} fontSize={'11px'}>
                                (En letras)
                            </Box>{' '}
                            {numeroPorExtensoEspanhol(
                                totals?.total,
                                selectedCoin
                            )}
                        </Typography>
                        <Stack direction={'row'} spacing={10}>
                            <Typography sx={{ fontSize: '12px' }}>
                                Liquidación del IVA:{' '}
                            </Typography>
                            <Typography sx={{ fontSize: '12px' }}>
                                (5%):{' '}
                                {maskCurrency(
                                    totals?.totalIvaFivePercent as number,
                                    selectedCoin
                                )}
                            </Typography>
                            <Typography sx={{ fontSize: '12px' }}>
                                (10%):{' '}
                                {maskCurrency(
                                    totals?.totalIvaTenPercent as number,
                                    selectedCoin
                                )}
                            </Typography>
                            <Typography sx={{ fontSize: '12px' }}>
                                (T. IVA):{' '}
                                {maskCurrency(
                                    totals?.totalIva as number,
                                    selectedCoin
                                )}
                            </Typography>
                        </Stack>
                        <Typography fontSize={'12px'}>
                            {clientVia
                                ? 'Original: Cliente'
                                : 'Duplicado: Tributário'}
                        </Typography>
                        {sale.observation && !sale.isJoinedNote && (
                            <Typography fontSize={'12px'}>
                                Nota: {sale.observation}
                            </Typography>
                        )}
                    </Stack>
                    <Box>
                        <Typography
                            fontSize={'12px'}
                            borderLeft={'1px solid black'}
                            borderBottom={'1px solid black'}
                            p={'5px'}
                        >
                            {maskCurrency(totals?.total, selectedCoin)}
                        </Typography>
                    </Box>
                </Stack>
            </Stack>
            {isEletronicNote && (
                <Stack
                    width={'100%'}
                    border={'1px solid black'}
                    borderTop={'0'}
                    borderRadius={'12px'}
                    p="10px"
                >
                    <Stack direction={'row'} spacing={'15px'}>
                        <Box component="canvas" ref={qrCodeRef} />
                        <Stack
                            direction="column"
                            py="10px"
                            /* spacing={'5px'} */ justifyContent="space-between"
                        >
                            <Stack>
                                <Typography fontSize="12px">
                                    Consulte la validez de esta factura
                                    eletrónica con el documento CDC impreso en:
                                </Typography>
                                <Typography fontSize="13px">
                                    https://ekuatia.set.gov.py/consultas/
                                </Typography>
                                <Typography fontSize="13px">
                                    CDC: {sale.cdc}
                                </Typography>
                            </Stack>
                            <Stack>
                                <Typography fontSize="11px">
                                    ESTE DOCUMENTO ES UNA REPRESENTACIÓN GRÁFICA
                                    DE UN DOCUMENTO ELETRÓNICO (XML)
                                </Typography>
                                <Typography fontWeight="bold" fontSize="12px">
                                    Información de interés del facturador
                                    eletrónico emisor
                                </Typography>
                                <Typography fontSize="11px">
                                    Si su documento eletrónico presenta algún
                                    error, podrá solicitar la modifcación dentro
                                    de las 72 horas siguientes de la emisión de
                                    éste comprobante
                                </Typography>
                            </Stack>
                        </Stack>
                    </Stack>
                </Stack>
            )}
            {!isEletronicNote && (
                <>
                    {sale.saleStatus === SaleStatus.Open &&
                        sale.paymentTypeShow === PaymentType.Credit &&
                        configs.promissoryNoteMode !==
                            PromissoryNodeMode.DontShow && (
                            <Stack
                                width={'100%'}
                                border={'1px solid black'}
                                borderTop={'0'}
                                borderRadius={'12px'}
                                pl="2px"
                                direction={'row'}
                                spacing={'15px'}
                            >
                                <Typography fontSize={'10px'}>
                                    La falta de pago de esta factura a su
                                    vencimiento, constituirá en mora al deudor y
                                    generara en forma automática todos los
                                    intereses correspondientes, autorizando a la
                                    consulta como a la inclusión a la Base de
                                    Datos de Informaciones Comerciales conforme
                                    a lo establecido en la Ley 6534/20, como
                                    también para que se pueda proveer
                                    información a terceros interesados.
                                </Typography>
                            </Stack>
                        )}
                    <Stack
                        width={'100%'}
                        border={'1px solid black'}
                        borderTop={'0'}
                        borderRadius={'12px'}
                        px="10px"
                        pt="20px"
                        direction={'row'}
                        spacing={'15px'}
                    >
                        <Typography fontSize="11px">
                            <Box component={'span'} fontWeight={'bold'}>
                                Firma:
                            </Box>{' '}
                            ______________________________
                        </Typography>
                        <Typography fontSize="11px">
                            <Box component={'span'} fontWeight={'bold'}>
                                Aclaración:
                            </Box>{' '}
                            ______________________________
                        </Typography>
                        <Typography fontSize="11px">
                            <Box component={'span'} fontWeight={'bold'}>
                                Fecha:
                            </Box>{' '}
                            ______________________________
                        </Typography>
                    </Stack>
                </>
            )}
        </Stack>
    )
}

interface Props {
    sales: SaleInfo[]
    handleClose(): void
    selectedCoin?: CoinInfo
    dontPrint?: boolean
}

export function LegalNoteDialog({
    handleClose,
    sales,
    selectedCoin,
    dontPrint,
}: Props) {
    const { configs } = useConfigs()
    const printRef = useRef<HTMLElement>()
    const elementsRendered = useRef(0)
    const count = useRef(0)

    const elementsToRender = sales.length * (!dontPrint ? configs.notesQnt : 1)

    const handleFinalizedRendering = () => {
        elementsRendered.current++

        if (elementsRendered.current !== elementsToRender) return
        if (!printRef.current) return

        if (configs.printMethod === PrintMethodEnum.Complete) {
            const twoPages = !configs.printHalfPage || dontPrint

            for (let child in printRef.current!.children) {
                if (isNaN(Number(child))) continue
                const element = printRef.current!.children[child] as HTMLElement

                const a4HeightPx = 1122
                let heightInVh = (element.offsetHeight / a4HeightPx) * 100

                count.current++

                if (heightInVh < 50 && !twoPages) {
                    heightInVh = 50
                } else {
                    heightInVh = 100 * (Math.floor((heightInVh - 1) / 100) + 1)
                }

                element.style.height = `${heightInVh}vh`
            }
        }

        if (!dontPrint) {
            printRef.current!.style.visibility = 'hidden'
            printRef.current!.style.position = 'fixed'

            setTimeout(() => {
                printRef.current!.style.position = 'absolute'
                printRef.current!.className = 'printable'
                window.scroll(0, 0)
                window.print()
                handleClose()
            }, 250)
        }

        printRef.current!.id = 'note'
    }

    return (
        <Box ref={printRef}>
            {sales.map((sale) => {
                return (
                    <>
                        {Array.from({
                            length: !dontPrint ? configs.notesQnt : 1,
                        }).map((_, index) => (
                            <LegalNote
                                sale={sale}
                                clientVia={index % 2 === 0}
                                index={index}
                                selectedCoin={selectedCoin ?? sale.coin}
                                hasRendered={handleFinalizedRendering}
                            />
                        ))}
                    </>
                )
            })}
        </Box>
    )
}
